const glossySelections = new Map();
const glossyTransitions = new Map();
let glossyCheckoutPending = false;

// Definition readiness is independent of the elapsed time since a trim was chosen.
const glossyModulesReady = Promise.all([
  'variant-picker', 'product-form-component', 'add-to-cart-component'
].map((name) => customElements.whenDefined(name)));

function glossySelector(productId = '') {
  return [...document.querySelectorAll('astra-grade-selector[data-aa-trim-only]')]
    .find((selector) => !productId || selector.dataset.productId === productId);
}

function glossyText(selector, key) {
  try {
    return JSON.parse(selector.querySelector('[data-aa-text]')?.textContent || '{}')[key] || '';
  } catch {
    return '';
  }
}

function glossyVariants(selector) {
  try {
    return JSON.parse(selector.querySelector('[data-aa-variants]')?.textContent || '[]');
  } catch {
    return [];
  }
}

function glossyVariant(selector, trim) {
  if (!trim) return null;
  return glossyVariants(selector).find((variant) =>
    variant.option1 === trim && (!variant.option2 || variant.option2 === 'Clear glass')
  ) || null;
}

function glossyMoney(cents) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(cents || 0) / 100);
}

function glossySelection(selector) {
  if (!glossySelections.has(selector.dataset.productId)) {
    glossySelections.set(selector.dataset.productId, selector.querySelector('[data-aa-grade]')?.value || '');
  }
  return glossySelections.get(selector.dataset.productId) || '';
}

function glossySetText(element, value) {
  if (element && element.textContent !== value) element.textContent = value;
}

function glossyForms(selector) {
  return [...document.querySelectorAll('product-form-component')]
    .filter((component) => component.dataset.productId === selector.dataset.productId)
    .map((component) => component.querySelector('form[data-type="add-to-cart-form"]'))
    .filter(Boolean);
}

function glossyTransition(selector) {
  const productId = selector.dataset.productId;
  if (!glossyTransitions.has(productId)) {
    glossyTransitions.set(productId, { generation: 0, pending: false, failed: false });
  }
  return glossyTransitions.get(productId);
}

function glossyPicker(selector) {
  return [...document.querySelectorAll('variant-picker')]
    .find((picker) => picker.dataset.productId === selector.dataset.productId)
    || selector.closest('.shopify-section')?.querySelector('variant-picker');
}

function glossyComponentsReady(selector) {
  const picker = glossyPicker(selector);
  const forms = glossyForms(selector);
  return Boolean(picker && typeof picker.variantChanged === 'function' && forms.length && forms.every((form) => {
    const component = form.closest('product-form-component');
    const add = form.querySelector('add-to-cart-component');
    return typeof component?.handleSubmit === 'function'
      && typeof add?.enable === 'function'
      && component.refs?.variantId === form.querySelector('input[name="id"]')
      && add.refs?.addToCartButton?.isConnected;
  }));
}

function glossyVariantPending(selector) {
  return glossyTransition(selector).pending || !glossyComponentsReady(selector);
}

function glossyAddPending(selector) {
  return glossyForms(selector).some((form) => form.closest('product-form-component')?.dataset.aaPurchasePending === 'true');
}

function glossyPurchaseReady(selector) {
  const transition = glossyTransition(selector);
  const variant = glossyVariant(selector, glossySelection(selector));
  return Boolean(variant?.available && !glossyCheckoutPending && !glossyAddPending(selector)
    && !glossyVariantPending(selector) && !transition.failed
    && transition.available !== false);
}

function glossyProperty(form, name, value) {
  const key = name.toLowerCase().replaceAll(' ', '-');
  let input = form.querySelector(`input[data-aa-property="${key}"]`)
    || form.querySelector(`input[name="properties[${name}]"]`);
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.dataset.aaProperty = key;
    form.appendChild(input);
  }
  input.dataset.aaProperty = key;
  input.value = value || '';
  input.disabled = !value;
}

function glossySyncForms(selector, trim, variant) {
  glossyForms(selector).forEach((form) => {
    const idInput = form.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = variant?.id || '';
      idInput.setAttribute('value', variant?.id || '');
    }
    glossyProperty(form, 'RAV4 trim', trim);
    glossyProperty(form, 'Estimated standard delivery', variant ? glossyText(selector, 'delivery_standard') : '');
    form.querySelectorAll('input[data-aa-property="glass-finish"], input[data-aa-property="rav4-grade"]')
      .forEach((input) => { input.disabled = true; });
  });
}

function glossyPurchaseState(selector, enabled, soldOut = false) {
  const variantPending = glossyVariantPending(selector);
  const addPending = glossyAddPending(selector);
  enabled = enabled && !addPending;
  const busy = glossyCheckoutPending || variantPending || addPending;
  const buttonText = glossyText(selector, glossyCheckoutPending ? 'opening_checkout'
    : variantPending ? 'updating_selection' : 'buy_now');
  const addText = glossyText(selector,
    variantPending ? 'updating_selection'
      : glossyCheckoutPending || addPending || enabled ? 'add_to_cart' : soldOut ? 'sold_out' : 'choose_trim_button'
  );
  glossyForms(selector).forEach((form) => {
    const component = form.closest('product-form-component');
    if (component) {
      if (variantPending) component.dataset.aaVariantPending = 'true';
      else component.removeAttribute('data-aa-variant-pending');
    }
    form.setAttribute('aria-busy', String(busy));
    const buyNow = form.querySelector('[data-aa-buy-now]');
    if (buyNow) {
      buyNow.disabled = !enabled;
      buyNow.setAttribute('aria-disabled', String(buyNow.disabled));
      buyNow.setAttribute('aria-busy', String(glossyCheckoutPending && buyNow.dataset.aaPurchasePending === 'true'));
      glossySetText(buyNow, buttonText);
    }
    const add = form.querySelector('[data-testid="standalone-add-to-cart"]');
    if (add) {
      add.disabled = !enabled;
      add.setAttribute('aria-disabled', String(!enabled));
      add.setAttribute('aria-busy', String(addPending && add.dataset.aaPurchasePending === 'true'));
      const label = add.querySelector('.add-to-cart-text__content > span:first-child');
      if (label) glossySetText(label, addText);
    }
  });
  const stickyBar = document.querySelector('.sticky-add-to-cart__bar');
  if (stickyBar) stickyBar.hidden = !enabled;
  const stickyButton = document.querySelector('.sticky-add-to-cart__button');
  if (stickyButton) stickyButton.disabled = !enabled;
}

function glossyRender(selector) {
  const trim = glossySelection(selector);
  const select = selector.querySelector('[data-aa-grade]');
  const variant = glossyVariant(selector, trim);
  const complete = Boolean(trim && variant);
  const transition = glossyTransition(selector);
  const pending = glossyVariantPending(selector);
  const available = Boolean(variant?.available && transition.available !== false);
  if (select) {
    if (select.value !== trim) select.value = trim;
    select.disabled = glossyCheckoutPending || glossyAddPending(selector);
  }
  selector.toggleAttribute('data-grade-selected', Boolean(trim));
  selector.toggleAttribute('data-selection-complete', complete);
  selector.dataset.state = !complete ? '' : pending ? 'updating'
    : transition.failed ? 'error' : available ? 'stock' : 'soldout';
  const summary = selector.querySelector('[data-aa-price-summary]');
  const delivery = selector.querySelector('[data-aa-delivery]');
  if (summary) summary.hidden = !complete;
  if (delivery) delivery.hidden = !available || pending || transition.failed;
  glossySetText(selector.querySelector('[data-aa-step]'),
    glossyText(selector, pending && trim ? 'updating_selection'
      : complete && !transition.failed ? 'step_ready' : 'step_trim_only'));
  glossySetText(selector.querySelector('[data-aa-badge]'),
    glossyText(selector, !trim || transition.failed ? 'required'
      : pending ? 'updating_selection' : available ? 'in_stock' : 'sold_out'));
  glossySetText(selector.querySelector('[data-aa-note]'),
    !trim ? glossyText(selector, 'choose_grade') : pending
      ? glossyText(selector, 'updating_selection') : transition.failed
      ? glossyText(selector, 'selection_error') : available
      ? glossyText(selector, 'selected_trim').replace('__TRIM__', trim)
      : glossyText(selector, 'unavailable'));
  if (variant) {
    glossySetText(selector.querySelector('[data-aa-final-price]'), glossyMoney(variant.price));
    glossySetText(selector.querySelector('[data-aa-shipping-label]'), glossyText(selector, 'delivery_label'));
    glossySetText(selector.querySelector('[data-aa-shipping-estimate]'), glossyText(selector, 'delivery_standard'));
    selector.closest('.shopify-section')?.querySelectorAll('[data-aa-intro-price]').forEach((element) => {
      const from = element.querySelector('[data-aa-price-from]');
      if (from) from.hidden = true;
      glossySetText(element.querySelector('[data-aa-intro-amount]'), glossyMoney(variant.price));
    });
  }
  // Do not overwrite the form with a locally predicted variant while native rendering is in flight.
  if (!pending && !transition.failed) glossySyncForms(selector, trim, variant);
  glossyPurchaseState(selector, glossyPurchaseReady(selector), complete && !available);
}

function glossyChooseNativeTrim(selector, trim, force = false) {
  const picker = glossyPicker(selector);
  if (!picker || !trim) return false;
  const radio = [...picker.querySelectorAll('input[type="radio"]')]
    .find((input) => input.dataset.optionName === 'RAV4 trim' && input.value === trim);
  if (radio) {
    if (!radio.checked) radio.click();
    else if (force) radio.dispatchEvent(new Event('change', { bubbles: true }));
    else return false;
    return true;
  }
  const select = [...picker.querySelectorAll('select')]
    .find((input) => input.dataset.optionName === 'RAV4 trim'
      || [...input.options].some((option) => option.dataset.optionName === 'RAV4 trim'));
  if (select && (select.value !== trim || force)) {
    select.value = trim;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  return false;
}

function glossyNativeVariantId(selector) {
  try {
    return JSON.parse(glossyPicker(selector)?.querySelector('script[type="application/json"]')?.textContent || 'null')?.id;
  } catch { return null; }
}

async function glossySelectTrim(selector, trim) {
  const productId = selector.dataset.productId;
  const transition = glossyTransition(selector);
  const retry = transition.failed;
  const generation = ++transition.generation;
  transition.pending = Boolean(trim);
  transition.failed = false;
  delete transition.available;
  glossyRender(selector);
  if (!trim) return;

  await glossyModulesReady;
  const current = glossySelector(productId);
  if (!current || transition.generation !== generation || glossySelection(current) !== trim) return;
  try {
    const expected = glossyVariant(current, trim);
    const alreadyResolved = String(glossyNativeVariantId(current)) === String(expected?.id);
    const dispatched = glossyChooseNativeTrim(current, trim, retry || !alreadyResolved);
    if (dispatched && transition.generation !== generation) return;
    transition.pending = false;
    transition.failed = dispatched || !alreadyResolved;
  } catch {
    transition.pending = false;
    transition.failed = true;
  }
  glossyRender(current);
}

// The producer resolves this promise only after native picker morphing. This bubbling
// listener runs after the product-form's section listener, so its continuation follows
// the form's ID/button/ref update and finally block; a microtask also covers ref observers.
document.addEventListener('shopify:product:select', async (event) => {
  const picker = event.target?.closest?.('variant-picker');
  const selector = picker && glossySelector(picker.dataset.productId);
  if (!selector || picker !== glossyPicker(selector) || !event.promise) return;
  const trim = glossySelection(selector);
  if (!trim) return;
  const transition = glossyTransition(selector);
  const generation = ++transition.generation;
  transition.pending = true;
  transition.failed = false;
  glossyRender(selector);
  let result;
  try { result = await event.promise; } catch { /* The latest failed transition stays non-purchasable. */ }
  await Promise.resolve();
  const current = glossySelector(selector.dataset.productId);
  if (!current) return;
  if (transition.generation !== generation || glossySelection(current) !== trim) {
    // Even an older native response may have touched its button. Reassert the latest gate.
    glossyRender(current);
    return;
  }
  const resource = result?.detail?.resource;
  const expected = glossyVariant(current, trim);
  transition.pending = false;
  transition.failed = !resource || String(resource.id) !== String(expected?.id);
  transition.available = resource?.available;
  glossyRender(current);
});

function glossyGuide(selector) {
  const select = selector.querySelector('[data-aa-grade]');
  const note = selector.querySelector('[data-aa-note]');
  if (note && select) {
    note.id ||= `${selector.id}-selection-note`;
    select.setAttribute('aria-describedby', note.id);
  }
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  select?.scrollIntoView({ block: 'center', behavior });
  select?.focus({ preventScroll: true });
}

document.addEventListener('change', (event) => {
  const select = event.target.closest?.('astra-grade-selector[data-aa-trim-only] [data-aa-grade]');
  if (!select) return;
  const selector = select.closest('astra-grade-selector');
  const trim = select.value;
  glossySelections.set(selector.dataset.productId, trim);
  glossySelectTrim(selector, trim);
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest?.('[data-aa-buy-now]');
  if (!button) return;
  const form = button.closest('form[data-type="add-to-cart-form"]');
  const selector = glossySelector(form?.closest('product-form-component')?.dataset.productId);
  if (!selector) return;
  event.preventDefault();
  if (glossyCheckoutPending || button.disabled || !glossyPurchaseReady(selector)) return;
  glossyRender(selector);
  if (!selector.hasAttribute('data-selection-complete')) {
    glossyGuide(selector);
    return;
  }
  if (!form.reportValidity()) return;
  const formData = new FormData(form);
  if (!formData.get('id')) return;
  const error = form.querySelector('[data-aa-buy-now-error]');
  if (error) { error.hidden = true; error.textContent = ''; }
  glossyCheckoutPending = true;
  button.dataset.aaPurchasePending = 'true';
  glossyRender(selector);
  let added = false;
  try {
    const response = await fetch(button.dataset.addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: formData
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(typeof result.description === 'string' ? result.description : glossyText(selector, 'checkout_error'));
    }
    added = true;
    document.dispatchEvent(new CustomEvent('aa:commerce-result', { detail: { name: 'buy_now_cart_success', product_id: selector.dataset.productId, variant_id: formData.get('id'), quantity: Number(formData.get('quantity')) || 1 } }));
    window.location.assign(button.dataset.checkoutUrl);
  } catch (failure) {
    document.dispatchEvent(new CustomEvent('aa:commerce-result', { detail: { name: 'buy_now_error', product_id: selector.dataset.productId, variant_id: formData.get('id'), quantity: Number(formData.get('quantity')) || 1 } }));
    if (error) {
      error.textContent = failure.message || glossyText(selector, 'checkout_error');
      error.hidden = false;
    }
  } finally {
    if (!added) {
      glossyCheckoutPending = false;
      button.removeAttribute('data-aa-purchase-pending');
      glossyRender(selector);
    }
  }
});

document.addEventListener('pointerdown', (event) => {
  if (!event.target.closest?.('[data-testid="standalone-add-to-cart"], .sticky-add-to-cart__button')) return;
  const selector = glossySelector();
  if (selector) glossyRender(selector);
}, true);

document.addEventListener('submit', (event) => {
  if (!event.target.matches?.('form[data-type="add-to-cart-form"]')) return;
  const selector = glossySelector(event.target.closest('product-form-component')?.dataset.productId);
  if (!selector) return;
  glossyRender(selector);
  const variant = glossyVariant(selector, glossySelection(selector));
  if (!variant?.available || !glossyPurchaseReady(selector)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!variant?.available) glossyGuide(selector);
  }
}, true);

function glossyHydrate() {
  document.querySelectorAll('astra-grade-selector[data-aa-trim-only]').forEach(glossyRender);
}

document.addEventListener('aa:purchase-pending-change', glossyHydrate);
glossyHydrate();
glossyModulesReady.then(glossyHydrate);
let glossyHydrateQueued = false;
new MutationObserver((mutations) => {
  if (!mutations.some((mutation) => [...mutation.addedNodes].some((node) =>
    node.nodeType === Node.ELEMENT_NODE && (
      node.matches?.('astra-grade-selector, product-form-component, variant-picker') ||
      node.querySelector?.('astra-grade-selector, product-form-component, variant-picker')
    )
  ))) return;
  if (glossyHydrateQueued) return;
  glossyHydrateQueued = true;
  queueMicrotask(() => {
    glossyHydrateQueued = false;
    glossyHydrate();
  });
}).observe(document.body, { childList: true, subtree: true });

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  glossyCheckoutPending = false;
  document.querySelectorAll('[data-aa-buy-now][data-aa-purchase-pending]').forEach((button) => button.removeAttribute('data-aa-purchase-pending'));
  glossyHydrate();
});
